import type { ReactNode } from "react";
import "./PageHeader.css";

/**
 * The bar at the top of a page: title on the left, controls on the right.
 *
 * Extracted because the same recipe — one topbar-height row, `--sp-4` of
 * horizontal padding, a bottom rule, panel background — had been written out
 * eleven times across eight stylesheets. Most copies agreed; Schedule and Feed
 * had drifted, which is what made the pages look subtly unlike each other.
 *
 * One definition means a change lands everywhere, and a new page inherits the
 * convention instead of copying whichever neighbour was open at the time.
 */

export interface PageHeaderProps {
  title: ReactNode;
  /** Sits beside the title — a count, a status, a breadcrumb. */
  meta?: ReactNode;
  /** Controls aligned to the right: filters, view switches, primary actions. */
  actions?: ReactNode;
  /** Rendered before the title, e.g. a back button on a detail page. */
  leading?: ReactNode;
  className?: string;
}

export function PageHeader({ title, meta, actions, leading, className }: PageHeaderProps) {
  return (
    <header className={["ui-page-header", className ?? ""].filter(Boolean).join(" ")}>
      {leading && <div className="ui-page-header__leading">{leading}</div>}

      <div className="ui-page-header__titles">
        {/* Always an h1: one page, one top-level heading, so the document
            outline is right without each caller remembering. */}
        <h1 className="ui-page-header__title">{title}</h1>
        {meta && <span className="ui-page-header__meta">{meta}</span>}
      </div>

      {actions && <div className="ui-page-header__actions">{actions}</div>}
    </header>
  );
}
